import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import NewArrivals from '../components/NewArrivals';
import BestSellers from '../components/BestSellers';
import PromotionSection from '../components/PromotionSection';
import LogoLoader from '../components/ui/LogoLoader';
import TrendingSection from '../components/TrendingSection';
import ScrollAnimationWrapper from '../components/ScrollAnimationWrapper';

const Home: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Carousel slides data - loaded from API
  const [carouselSlides, setCarouselSlides] = useState<any[]>([]);

  // Track whether we're on a small (mobile) screen so the carousel can use a
  // mobile-specific banner image when the admin uploaded one.
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const loadBanners = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/banners/active`);
        const data = await response.json();
        setCarouselSlides(data || []);
      } catch (error) {
        // Use fallback banners if API fails
        setCarouselSlides([
          {
            id: 1,
            title: 'SPECIAL PROMOTION',
            subtitle: 'Promo Code: SSNEWYEAR',
            discount_text: '10% OFF',
            image_url: `${process.env.PUBLIC_URL}/banner/banner.jpg`,
            bg_color: 'from-orange-500 to-red-500',
            text_color: 'text-white',
            badge_color: 'bg-white text-orange-500',
            button_text: 'Shop Now',
            button_link: '/product-signature'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadBanners();
  }, []);

  // Enhanced carousel auto-play with progress tracking
  const startCarousel = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    
    setProgress(0);
    
    // Progress bar animation
    progressIntervalRef.current = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          return 0;
        }
        return prev + (100 / 80); // 8000ms / 100ms intervals
      });
    }, 100);
    
    // Slide change
    intervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
      setProgress(0);
    }, 8000);
  }, [carouselSlides.length]);

  const stopCarousel = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
  }, []);

  useEffect(() => {
    if (!isCarouselPaused) {
      startCarousel();
    } else {
      stopCarousel();
    }

    return () => {
      stopCarousel();
    };
  }, [isCarouselPaused, startCarousel, stopCarousel]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
    setProgress(0);
    if (!isCarouselPaused) {
      startCarousel();
    }
  }, [carouselSlides.length, isCarouselPaused, startCarousel]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + carouselSlides.length) % carouselSlides.length);
    setProgress(0);
    if (!isCarouselPaused) {
      startCarousel();
    }
  }, [carouselSlides.length, isCarouselPaused, startCarousel]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
    setProgress(0);
    if (!isCarouselPaused) {
      startCarousel();
    }
  }, [isCarouselPaused, startCarousel]);

  // Touch/swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  // Keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      prevSlide();
    } else if (e.key === 'ArrowRight') {
      nextSlide();
    } else if (e.key === ' ') {
      e.preventDefault();
      setIsCarouselPaused(prev => !prev);
    }
  }, [nextSlide, prevSlide]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);





  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LogoLoader size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Promotional Banner Carousel */}
      <section className="relative overflow-hidden">
        <div 
          className="relative h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] xl:h-[700px] group"
          onMouseEnter={() => setIsCarouselPaused(true)}
          onMouseLeave={() => setIsCarouselPaused(false)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          role="region"
          aria-label="Promotional carousel"
          tabIndex={0}
        >
          {/* Slides */}
          {carouselSlides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-all duration-700 ease-out transform ${
                index === currentSlide 
                  ? 'translate-x-0 opacity-100 scale-100' 
                  : index < currentSlide 
                    ? '-translate-x-full opacity-0 scale-95' 
                    : 'translate-x-full opacity-0 scale-95'
              }`}
              aria-hidden={index !== currentSlide}
            >
              <div className={`relative h-full py-12 overflow-hidden`}>
                {/* Background Image */}
                <div 
                  className="absolute inset-0"
                  style={{
                    backgroundImage: `url(${
                      (isMobile && slide.mobile_image_url)
                        ? slide.mobile_image_url
                        : (slide.image_url || `${process.env.PUBLIC_URL}/banner/banner.jpg`)
                    })`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                >
                </div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 h-full flex items-center">
                  <div className="text-left max-w-xl">
                    {slide.title && (
                      <div className="inline-block animate-fadeInUp" style={{animationDelay: '0.2s'}}>
                        <span className={`${slide.badge_color || 'bg-white text-blue-500'} px-4 py-2 rounded-full text-sm font-bold mb-6 inline-block shadow-lg transform hover:scale-105 transition-transform`}>
                          {slide.title}
                        </span>
                      </div>
                    )}
                    {slide.subtitle && (
                      <h1 className="text-4xl md:text-6xl font-bold mb-4 animate-fadeInUp text-white drop-shadow-lg" style={{animationDelay: '0.4s'}}>
                        {slide.subtitle}
                      </h1>
                    )}
                    {slide.discount_text && (
                      <p className="text-2xl md:text-4xl font-bold mb-8 animate-fadeInUp text-white drop-shadow-lg" style={{animationDelay: '0.6s'}}>
                        {slide.discount_text}
                      </p>
                    )}
                  </div>
                </div>
                {/* CTA button: anchored to the bottom-left of the slide so it
                    stays in a fixed position across all slides and does not
                    overlap text designed into the banner image. */}
                {slide.button_text && (
                  <div className="absolute bottom-6 sm:bottom-8 left-0 right-0 z-10">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                      <Link
                        to={slide.button_link || '/product-signature'}
                        className="group/btn inline-flex items-center gap-2 px-6 py-3 sm:px-8 sm:py-4 rounded-full font-semibold text-white border border-white/30 bg-white/15 backdrop-blur-md shadow-[0_8px_32px_rgba(0,0,0,0.25)] ring-1 ring-inset ring-white/20 hover:bg-white/25 hover:border-white/50 hover:shadow-[0_8px_40px_rgba(255,255,255,0.25)] transform hover:scale-105 transition-all duration-300 animate-fadeInUp"
                        style={{animationDelay: '0.8s', WebkitBackdropFilter: 'blur(12px)'}}
                      >
                        {slide.button_text}
                        <svg className="inline-block w-5 h-5 transition-transform duration-300 group-hover/btn:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-40 text-white p-3 rounded-full transition-all duration-300 z-20 opacity-0 group-hover:opacity-100 hover:scale-110 backdrop-blur-sm shadow-lg"
            aria-label="Previous slide"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-40 text-white p-3 rounded-full transition-all duration-300 z-20 opacity-0 group-hover:opacity-100 hover:scale-110 backdrop-blur-sm shadow-lg"
            aria-label="Next slide"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Play/Pause Button */}
          <button
            onClick={() => setIsCarouselPaused(prev => !prev)}
            className="absolute top-4 right-4 bg-white bg-opacity-20 hover:bg-opacity-40 text-white p-2 rounded-full transition-all duration-300 z-20 opacity-0 group-hover:opacity-100 backdrop-blur-sm shadow-lg"
            aria-label={isCarouselPaused ? 'Play carousel' : 'Pause carousel'}
          >
            {isCarouselPaused ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
              </svg>
            )}
          </button>

          {/* Enhanced Dots Indicator with Progress */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-20">
            <div className="flex items-center space-x-3 bg-black bg-opacity-20 px-4 py-2 rounded-full backdrop-blur-sm">
              {carouselSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`relative w-3 h-3 rounded-full transition-all duration-300 hover:scale-125 ${
                    index === currentSlide 
                      ? 'bg-white shadow-lg' 
                      : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                >
                  {index === currentSlide && (
                    <div 
                      className="absolute inset-0 rounded-full border-2 border-white"
                      style={{
                        background: `conic-gradient(from 0deg, transparent ${progress * 3.6}deg, rgba(255,255,255,0.3) ${progress * 3.6}deg)`
                      }}
                    />
                  )}
                </button>
              ))}
            </div>
            
            {/* Slide Counter */}
            <div className="text-center mt-2">
              <span className="text-white text-sm font-medium bg-black bg-opacity-20 px-3 py-1 rounded-full backdrop-blur-sm">
                {currentSlide + 1} / {carouselSlides.length}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Best Seller Section */}
      <ScrollAnimationWrapper animation="fadeInUp" delay={100} duration={0.8}>
        <BestSellers />
      </ScrollAnimationWrapper>

      {/* Promotion Section */}
      <ScrollAnimationWrapper animation="fadeInLeft" delay={200} duration={0.8}>
        <PromotionSection />
      </ScrollAnimationWrapper>

      {/* New Arrivals Section */}
      <ScrollAnimationWrapper animation="fadeInRight" delay={100} duration={0.8}>
        <NewArrivals />
      </ScrollAnimationWrapper>

      {/* Trending Section */}
      <ScrollAnimationWrapper animation="scaleIn" delay={150} duration={0.9}>
        <TrendingSection />
      </ScrollAnimationWrapper>
    </div>
  );
};

export default Home;
